/**
 * The studio, production and playout routes of the route table (`routes.ts`
 * spreads these into `ROUTES`): PLAN §5.1's Studio → Production, §5.3's
 * operator commands and §6.6's playout state and as-run log.
 *
 * Served by BOTH hosts through `@acp/handlers` (studios and productions are
 * stored on each; the node's replicate upward with the site link, phase 4).
 * The playout routes reach the production process through the
 * `PlayoutGateway` port: on a node, the local process (wave 3b); in the cloud,
 * nothing yet — they answer `unavailable` until the site link can carry a
 * command to a site's production (phase 4).
 *
 * Every permission is checked on the resource (D14): a studio is its own
 * resource (`studioId`, `siteId`), and a production is its studio's.
 *
 * Typed with `satisfies` against a type-only import, as `tenancyRoutes.ts`.
 */
import {
  asRunQueryWireSchema,
  asRunResponseWireSchema,
  commandResultWireSchema,
  playoutCommandWireSchema,
  playoutStateResponseWireSchema,
  PLAYOUT_COMMAND_PERMISSIONS,
} from './playout.js';
import {
  createProductionRequestWireSchema,
  endProductionRequestWireSchema,
  listProductionsQueryWireSchema,
  listProductionsResponseWireSchema,
  productionPathWireSchema,
  productionResponseWireSchema,
} from './productions.js';
import { BOTH_HOSTS } from './routeHosts.js';
import type { RouteDef } from './routes.js';
import {
  createShowStyleRequestWireSchema,
  deleteShowStyleQueryWireSchema,
  listShowStylesResponseWireSchema,
  showStylePathWireSchema,
  showStyleResponseWireSchema,
  updateShowStyleRequestWireSchema,
  validatePairQueryWireSchema,
  validatePairResponseWireSchema,
} from './showStyles.js';
import {
  createStudioRequestWireSchema,
  deleteStudioQueryWireSchema,
  listStudiosResponseWireSchema,
  studioPathWireSchema,
  studioResponseWireSchema,
  updateStudioRequestWireSchema,
} from './studios.js';

const CONFIG_VALIDATION =
  'The config is validated against the studio-config schema of the engine (devices, mappings from show-style layers to devices, settings; D32); a failure is `validation-failed` with issues under `config.`. `defaultShowStyleId` must name an existing show style (`validation-failed` otherwise).';

const SHOW_STYLE_VALIDATION =
  'The config is validated against the show-style schema of the engine (abstract layers, source/output layers, piece types, actions, adlib panels, the reserved `mosIngest`; D32); a failure is `validation-failed` with issues under `config.`.';

export const OPERATIONS_ROUTES = {
  createShowStyle: {
    id: 'createShowStyle',
    method: 'POST',
    path: '/show-styles',
    tag: 'show-styles',
    hosts: BOTH_HOSTS,
    summary: 'Create a show style',
    description: `Creates a show style (D32) at version 1 — a tenant-level library of piece types, layers, panels and actions, which studios map to their devices. Needs \`device.configure\` tenant-wide. Supplying \`showStyleId\` makes a retry idempotent. ${SHOW_STYLE_VALIDATION}`,
    params: null,
    query: null,
    body: createShowStyleRequestWireSchema,
    response: showStyleResponseWireSchema,
    successStatus: 201,
    permission: 'device.configure',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'conflict', 'payload-too-large'],
  },
  listShowStyles: {
    id: 'listShowStyles',
    method: 'GET',
    path: '/show-styles',
    tag: 'show-styles',
    hosts: BOTH_HOSTS,
    summary: 'List show styles',
    description: 'The tenant’s show styles, by id, without their configs. Any `rundown.read` grant may read them.',
    params: null,
    query: null,
    body: null,
    response: listShowStylesResponseWireSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: [],
  },
  getShowStyle: {
    id: 'getShowStyle',
    method: 'GET',
    path: '/show-styles/{showStyleId}',
    tag: 'show-styles',
    hosts: BOTH_HOSTS,
    summary: 'Get a show style',
    description: 'The show style with its config and the `version` an update must name.',
    params: showStylePathWireSchema,
    query: null,
    body: null,
    response: showStyleResponseWireSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found'],
  },
  updateShowStyle: {
    id: 'updateShowStyle',
    method: 'PUT',
    path: '/show-styles/{showStyleId}',
    tag: 'show-styles',
    hosts: BOTH_HOSTS,
    summary: 'Replace a show style',
    description: `Replaces name and config, if the show style is still at \`version\` (\`conflict\` otherwise: re-read, re-apply, retry), and bumps the version. Productions already created keep their snapshot (D22). ${SHOW_STYLE_VALIDATION}`,
    params: showStylePathWireSchema,
    query: null,
    body: updateShowStyleRequestWireSchema,
    response: showStyleResponseWireSchema,
    successStatus: 200,
    permission: 'device.configure',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  deleteShowStyle: {
    id: 'deleteShowStyle',
    method: 'DELETE',
    path: '/show-styles/{showStyleId}',
    tag: 'show-styles',
    hosts: BOTH_HOSTS,
    summary: 'Delete a show style',
    description:
      'Deletes the show style and returns it as it was. `?version=` makes it conditional. A show style that is a studio’s `defaultShowStyleId` is refused (`conflict`, naming the studios). Productions created from it are unaffected: they run from their own snapshot (D22).',
    params: showStylePathWireSchema,
    query: deleteShowStyleQueryWireSchema,
    body: null,
    response: showStyleResponseWireSchema,
    successStatus: 200,
    permission: 'device.configure',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found', 'conflict'],
  },
  validateShowStyle: {
    id: 'validateShowStyle',
    method: 'GET',
    path: '/show-styles/{showStyleId}/validate',
    tag: 'show-styles',
    hosts: BOTH_HOSTS,
    summary: 'Judge a show style against a studio',
    description:
      'Whether a production of this show style in `studioId` can be created (D32): every show-style layer a piece type uses mapped in the studio, to a device of a driver the layer allows, and every template’s `deviceType` the mapped device’s. `valid: false` with the problems (dotted paths starting `showStyle.` or `studio.`) is still a 200; `layers` is the per-layer mapping table. Needs `rundown.read` on the studio.',
    params: showStylePathWireSchema,
    query: validatePairQueryWireSchema,
    body: null,
    response: validatePairResponseWireSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found'],
  },
  createStudio: {
    id: 'createStudio',
    method: 'POST',
    path: '/studios',
    tag: 'studios',
    hosts: BOTH_HOSTS,
    summary: 'Create a studio',
    description: `Creates a studio at version 1. Supplying \`studioId\` makes a retry idempotent: a studio with that id is returned as it is. ${CONFIG_VALIDATION}`,
    params: null,
    query: null,
    body: createStudioRequestWireSchema,
    response: studioResponseWireSchema,
    successStatus: 201,
    permission: 'device.configure',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'conflict', 'payload-too-large'],
  },
  listStudios: {
    id: 'listStudios',
    method: 'GET',
    path: '/studios',
    tag: 'studios',
    hosts: BOTH_HOSTS,
    summary: 'List studios',
    description: 'The tenant’s studios the caller may read, by id, without their configs.',
    params: null,
    query: null,
    body: null,
    response: listStudiosResponseWireSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: [],
  },
  getStudio: {
    id: 'getStudio',
    method: 'GET',
    path: '/studios/{studioId}',
    tag: 'studios',
    hosts: BOTH_HOSTS,
    summary: 'Get a studio',
    description: 'The studio with its config and the `version` an update must name.',
    params: studioPathWireSchema,
    query: null,
    body: null,
    response: studioResponseWireSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found'],
  },
  updateStudio: {
    id: 'updateStudio',
    method: 'PUT',
    path: '/studios/{studioId}',
    tag: 'studios',
    hosts: BOTH_HOSTS,
    summary: 'Replace a studio',
    description: `Replaces name, site and config, if the studio is still at \`version\` (\`conflict\` otherwise: re-read, re-apply, retry), and bumps the version. Productions already created keep their snapshot (D22). ${CONFIG_VALIDATION}`,
    params: studioPathWireSchema,
    query: null,
    body: updateStudioRequestWireSchema,
    response: studioResponseWireSchema,
    successStatus: 200,
    permission: 'device.configure',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  deleteStudio: {
    id: 'deleteStudio',
    method: 'DELETE',
    path: '/studios/{studioId}',
    tag: 'studios',
    hosts: BOTH_HOSTS,
    summary: 'Delete a studio',
    description:
      'Deletes the studio and returns it as it was. `?version=` makes it conditional. Productions created from it are unaffected: they run from their own snapshot (D22).',
    params: studioPathWireSchema,
    query: deleteStudioQueryWireSchema,
    body: null,
    response: studioResponseWireSchema,
    successStatus: 200,
    permission: 'device.configure',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found', 'conflict'],
  },
  createProduction: {
    id: 'createProduction',
    method: 'POST',
    path: '/productions',
    tag: 'productions',
    hosts: BOTH_HOSTS,
    summary: 'Create a production',
    description:
      'Creates a production of a playlist (or a single rundown) in a studio with a show style (`showStyleId`, else the studio’s `defaultShowStyleId`; D32), status `created`. The pair is resolved into the production config it runs (`config`) and both documents are snapshotted as they are now (`snapshot`, D22); a pair that does not resolve is `validation-failed` with its problems (paths starting `showStyle.` or `studio.`), an unknown show style `not-found`. `packageHash` is null until the node runtime pins one (PLAN §4.1, phase 4). Supplying `productionId` makes a retry idempotent.',
    params: null,
    query: null,
    body: createProductionRequestWireSchema,
    response: productionResponseWireSchema,
    successStatus: 201,
    permission: 'production.launch.onprem',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  listProductions: {
    id: 'listProductions',
    method: 'GET',
    path: '/productions',
    tag: 'productions',
    hosts: BOTH_HOSTS,
    summary: 'List productions',
    description: 'The tenant’s productions the caller may read, newest first, without their config snapshots, in pages.',
    params: null,
    query: listProductionsQueryWireSchema,
    body: null,
    response: listProductionsResponseWireSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request'],
  },
  getProduction: {
    id: 'getProduction',
    method: 'GET',
    path: '/productions/{productionId}',
    tag: 'productions',
    hosts: BOTH_HOSTS,
    summary: 'Get a production',
    description: 'The production record with its config snapshot.',
    params: productionPathWireSchema,
    query: null,
    body: null,
    response: productionResponseWireSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found'],
  },
  endProduction: {
    id: 'endProduction',
    method: 'POST',
    path: '/productions/{productionId}/end',
    tag: 'productions',
    hosts: BOTH_HOSTS,
    summary: 'End a production',
    description:
      'Ends the production (terminal: it takes no more commands). An active production must be deactivated first (`conflict`), so nothing on air is ended by accident. Ending an ended production returns it as it is.',
    params: productionPathWireSchema,
    query: null,
    body: endProductionRequestWireSchema,
    response: productionResponseWireSchema,
    successStatus: 200,
    permission: 'production.launch.onprem',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict'],
  },
  sendPlayoutCommand: {
    id: 'sendPlayoutCommand',
    method: 'POST',
    path: '/productions/{productionId}/commands',
    tag: 'playout',
    hosts: BOTH_HOSTS,
    summary: 'Send an operator command',
    description:
      'One operator command (PLAN §5.3, D19) to the production’s active process: `activate`, `deactivate`, `reset` (need `playout.activate`), `take`, `setNext`, `moveNext`, `setNextSegment`, `queueNextSegment`, `adlib`, `adlibFromRow`, `stopPiecesOnLayer`, `disableNextPiece`, `executeAction`, `syncToInstance` (need `playout.take`). ' +
      'The answer is an ack with the playout `seq` that includes the command, or a nack with a `reason` — both 200. A repeated `commandId` gets the first answer again and is not run twice. ' +
      '`unavailable` when the production process cannot be reached — in the cloud always, until the site link (phase 4).',
    params: productionPathWireSchema,
    query: null,
    body: playoutCommandWireSchema,
    response: commandResultWireSchema,
    successStatus: 200,
    permission: null,
    permissionOneOf: PLAYOUT_COMMAND_PERMISSIONS,
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found', 'forbidden'],
  },
  getPlayout: {
    id: 'getPlayout',
    method: 'GET',
    path: '/productions/{productionId}/playout',
    tag: 'playout',
    hosts: BOTH_HOSTS,
    summary: 'The playout state',
    description:
      'The production’s playout state now (PLAN §5.2, §6.6): activation, previous/current/next part instances with their piece instances, timings, device statuses and the `seq`. Live changes: the WS `playout.subscribe`. `unavailable` when the production process cannot be reached (retry). `conflict` when the production has ended: it has no live state any more, and never will (its as-run log remains: `listAsRun`).',
    params: productionPathWireSchema,
    query: null,
    body: null,
    response: playoutStateResponseWireSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found', 'conflict'],
  },
  listAsRun: {
    id: 'listAsRun',
    method: 'GET',
    path: '/productions/{productionId}/asrun',
    tag: 'playout',
    hosts: BOTH_HOSTS,
    summary: 'The as-run log',
    description: 'The production’s append-only as-run events (PLAN §5.4), oldest first, after `cursor`.',
    params: productionPathWireSchema,
    query: asRunQueryWireSchema,
    body: null,
    response: asRunResponseWireSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found'],
  },
} as const satisfies Record<string, RouteDef>;
